/**
 * Default-renderer lookup for `dataSource` rows.
 *
 * A `dataSource` is alias-like: it owns cells transparently and
 * auto-hydrates a child renderer that paints its slot AND edits its
 * cells. The child is selected from a per-slot table keyed on
 * `(dtype, sourceShape, shape?, cellCardinality?)`; `shape` comes from
 * server metadata when present and can still be probe-derived for
 * formula/user rows.
 *
 * This module resolves the `rendererId` the hydrator should instantiate
 * as the source's default child. The table in this module is the
 * canonical lookup table.
 *
 * Scope for the current migration: only the `column` slot is wired.
 * Other slots (`canvas` / `timeline[row]` / `timeline[column]` /
 * `grid`) return `null` so the hydrator skips child synthesis
 * until their tables are ported — no regression for those layouts.
 *
 * `obj` dtypes also return `null`: obj sources are shape-keyed in the
 * architecture tables (Weather, Event, Slot, Report, TzClock, Sticker,
 * LogEntry, opaque). Typed access for those rows goes through facades
 * (`row.as(EVENTS_FACADE)` / `WEATHER_FACADE` / …); default-renderer
 * hydration for obj rows is out of scope here.
 */

import type { Option } from "../../core/option.ts";
import type { SourceDescriptor } from "../descriptor.ts";
import { RowShapes } from "../row-shapes.ts";
import { type LayoutKind, type RendererId, rendererIdOf } from "../slot.ts";

/**
 * Pick the renderer id a `dataSource` should auto-hydrate as a child
 * for the given slot. Returns `null` when no default is defined — the
 * hydrator then skips child synthesis for this source.
 *
 * Honors `descriptor.rendererOverride`: a user-set override short-
 * circuits the table and returns that renderer id directly.
 *
 * `shape` is the row's server-owned cell shape (`meta.shape`, e.g.
 * `"task/v1"`) — the principled key for obj sources, which are otherwise
 * indistinguishable by `(dtype, cardinality)` alone. Shape is never on
 * the descriptor (see `descriptor.ts`); the hydrator reads it from row
 * metadata and threads it here.
 */
export function resolveDefaultRendererId(
  d: SourceDescriptor,
  slot: LayoutKind,
  shape: Option<string>,
): Option<RendererId> {
  // A retired type's override (`RetiredRendererType`) names no renderer to
  // materialize: the row draws as an Unknown Plugin, with no renderer child.
  if (d.rendererOverride !== undefined) return rendererIdOf(d.rendererOverride);

  switch (slot) {
    case "column":
      return resolveColumn(d, shape);
    case "timeline[row]":
      return resolveTimeline(d, "timeline[row]");
    case "timeline[column]":
      return resolveTimeline(d, "timeline[column]");
    case "grid":
      return resolveGrid(d);
    case "canvas":
      // Every `canvas.*` renderer is a specialization — `(number,
      // series)` has four candidate outputs (points / line / bar /
      // regression-curve) and no default is defined in the
      // architecture catalog. Canvas children must be declared
      // explicitly; auto-hydration returns `null`.
      return null;
    default:
      // Structural pass-throughs (`group`, `block`, `header`, etc.)
      // are now `inert/*` rather than Layout members; if a caller
      // passes one (e.g. a fixture probing the negative cases), no
      // default renderer applies. Return `null` rather than fall
      // through to `undefined`.
      return null;
  }
}

function resolveColumn(
  d: SourceDescriptor,
  shape: Option<string>,
): Option<RendererId> {
  const { dtype, sourceShape: cardinality } = d;
  switch (dtype) {
    case "text":
      if (cardinality === "scalar") return "column.text";
      return null;
    case "boolean":
      if (cardinality === "scalar") return "column.checkmark";
      return null;
    case "number":
      if (cardinality === "scalar") return "column.number";
      if (cardinality === "per-cell") return "column.graph";
      if (cardinality === "series") return "column.series-summary";
      if (cardinality === "tensor") return "column.heatmap";
      return null;
    case "obj":
      // Shape-driven: an obj source whose server-owned `shape` metadata is
      // known materializes its typed renderer. The server is the source of
      // truth (shape lives in row metadata, not on the descriptor); the
      // client maps shape → UI renderer. This is the single home for the
      // obj-shape → column-renderer table (it replaced the render-time
      // `rendererHintForSource` hint).
      if (shape === RowShapes.WEATHER.key.id) return "column.weather";
      if (shape === RowShapes.TASK.key.id) return "column.task";
      if (shape === RowShapes.TIMEZONE.key.id) return "column.timezone";
      if (shape === RowShapes.LOGS.key.id) return "column.logs";
      // Sticker has no shape tag (custom cells), so it stays keyed on its
      // wire type (`objSubtype`) — the architecture table's
      // (obj, scalar, Sticker) → `column.sticker` row.
      if (d.objSubtype === "sticker" && cardinality === "scalar") {
        return "column.sticker";
      }
      // Other obj shapes are facade-accessed; no default renderer.
      return null;
  }
}

function resolveTimeline(
  d: SourceDescriptor,
  slot: "timeline[row]" | "timeline[column]",
): Option<RendererId> {
  const { dtype, sourceShape: cardinality } = d;
  switch (dtype) {
    case "text":
      if (cardinality === "scalar") return `${slot}.text` as const;
      return null;
    case "number":
      if (cardinality === "scalar") return `${slot}.number` as const;
      return null;
    case "obj":
      // `timeline[*].event` / `timeline[*].span` are shape-keyed
      // (Event / Slot). Skip auto-hydration — explicit declaration
      // only.
      return null;
    case "boolean":
      return null;
  }
}

function resolveGrid(d: SourceDescriptor): Option<RendererId> {
  const { dtype, sourceShape: cardinality } = d;
  switch (dtype) {
    case "text":
      if (cardinality === "scalar") return "grid.text";
      return null;
    case "obj":
      // `grid.event` is shape-keyed (Event). Skip auto-hydration.
      return null;
    case "number":
    case "boolean":
      return null;
  }
}
